<template>
    <template v-if="isVisible">
        <!-- Mobile: compact inline banner -->
        <BaseCard id="preferred_language_notice" padding="sm" class="md:hidden relative" data-testid="preferred-language-notice">
            <div class="flex items-center gap-3 pr-6">
                <div class="flex flex-col gap-1 min-w-0">
                    <div class="body-small text-ink-muted flex flex-wrap items-center gap-1">
                        {{ t("components.languageNotice.chatLanguageSetTo") }}
                        <StatusBadge :text="t('components.languageNotice.saved')" :is-showing="isSavedShowing" />
                        <BaseSelect v-model="selectedLanguageCode" :aria-label="t('components.languageNotice.selectLabel')" :disabled="isSaving" :options="languageOptions" />
                    </div>
                    <p class="details text-ink-muted">{{ t("components.languageNotice.changeAnytime") }}</p>
                </div>
            </div>
            <BaseButton
                theme="tertiary"
                icon-only
                size="sm"
                :aria-label="t('components.languageNotice.close')"
                class="top-2 right-1 absolute"
                data-testid="close-preferred-language-notice"
                @click="isVisible = false">
                <i class="bi bi-x-lg icon-bold text-xs" aria-hidden="true" />
            </BaseButton>
        </BaseCard>

        <!-- Desktop: compact fixed banner at bottom-right -->
        <BaseCard padding="sm" class="hidden md:block fixed bottom-0 right-0 mb-6 mx-6 max-w-xs z-30">
            <div class="flex items-center gap-3 pr-6">
                <div class="flex flex-col gap-1 min-w-0">
                    <div class="body-small text-ink-muted flex flex-wrap items-center gap-1">
                        {{ t("components.languageNotice.chatLanguageSetTo") }}
                        <StatusBadge :text="t('components.languageNotice.saved')" :is-showing="isSavedShowing" />
                        <BaseSelect v-model="selectedLanguageCode" :aria-label="t('components.languageNotice.selectLabel')" :disabled="isSaving" :options="languageOptions" />
                    </div>
                    <p class="details text-ink-muted">{{ t("components.languageNotice.changeAnytime") }}</p>
                </div>
            </div>
            <BaseButton
                theme="tertiary"
                icon-only
                size="sm"
                :aria-label="t('components.languageNotice.close')"
                class="top-2 right-1 absolute"
                data-testid="close-preferred-language-notice"
                @click="isVisible = false">
                <i class="bi bi-x-lg icon-bold text-xs" aria-hidden="true" />
            </BaseButton>
        </BaseCard>
    </template>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { computed, inject, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import StatusBadge from "./StatusBadge.vue";

const { t } = useI18n();

const userDetails = inject("user_details");

const isVisible = ref(false);
const isSaving = ref(false);
const { isShowing: isSavedShowing, show: showSaved } = useTimedShow({ initialState: false, duration: 2000 });
const choices = ref([]);
const languageOptions = computed(() => choices.value.map((choice) => ({ key: choice.code, label: choice.display })));
const selectedLanguageCode = ref("en");
const browserLanguageDisplay = ref("");

onMounted(async () => {
    const hasLanguage = userDetails.value.default_language && userDetails.value.default_language !== "no_default";
    if (hasLanguage) {
        return;
    }

    choices.value = await _fetchChoices();
    if (!choices.value) {
        return;
    }

    const codes = choices.value.map((choice) => choice.code);
    for (const longCode of navigator.languages) {
        const shortCode = longCode.slice(0, 2);
        if (codes.includes(shortCode)) {
            browserLanguageDisplay.value = _getLanguageDisplay(shortCode);
            selectedLanguageCode.value = shortCode;
            break;
        }
    }

    const ok = await _saveLanguagePreference(selectedLanguageCode.value);
    // If we couldn't save the initial browser setting, we shouldn't tell them we defaulted the
    // language at all.
    if (ok) {
        isVisible.value = true;
    }
});

watch(selectedLanguageCode, async (newCode) => {
    const ok = await _saveLanguagePreference(newCode);
    if (ok) {
        showSaved();
    }
});

function _getLanguageDisplay(code) {
    const choice = choices.value.find((c) => c.code == code);
    return choice?.display;
}

async function _fetchChoices() {
    const response = await fetch("/api/chat/language_choices");
    if (!response.ok) {
        console.error("Failed to get language choices for preferred language notice: " + response.status);
        return;
    }
    return await response.json();
}

async function _saveLanguagePreference(code) {
    if (isSaving.value) {
        return false;
    }

    const language = _getLanguageDisplay(code);
    if (!language) {
        return false;
    }

    isSaving.value = true;
    try {
        const response = await fetch("/accounts/profile/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
                detail: "default_language",
                default_language: language,
            }),
        });
        if (!response.ok) {
            console.error("Failed to save language preference: " + response.status);
        }
        return response.ok;
    } catch (err) {
        console.error("Failed to save language preference: " + err);
        return false;
    } finally {
        isSaving.value = false;
    }
}
</script>
